'use client';
import React from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { useModel } from '@/context/ModelContext';
import { fmtCrShort } from '@/lib/financial-model';

const COLORS = ['var(--gold)', 'var(--accent)', '#e8a020', '#8b7cf8', '#e05252'];

function CustomTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; payload: { amount: number } }>;
}) {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0];
  return (
    <div
      style={{
        background: 'var(--surface-2)',
        border: '1px solid var(--divider)',
        borderRadius: '4px',
        padding: '10px 14px',
      }}
    >
      <p style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-primary)' }}>{d.name}</p>
      <p
        style={{
          fontSize: '0.75rem',
          fontWeight: 700,
          fontFamily: 'var(--font-mono)',
          color: 'var(--text-primary)',
          marginTop: 4,
        }}
      >
        {fmtCrShort(d.payload.amount)}
      </p>
      <p style={{ fontSize: '0.65rem', color: 'var(--text-tertiary)' }}>
        {d.value.toFixed(1)}% of total
      </p>
    </div>
  );
}

export default function CapexBreakdownChart() {
  const { model } = useModel();
  const a = model.assumptions;
  const total = model.totalProjectCost;

  const items = [
    { name: 'Machinery', amount: a.machineryCapex },
    { name: 'Working Capital', amount: a.workingCapital },
    { name: 'Civil / Shed', amount: a.civilCapex },
    { name: 'Electrical', amount: a.electricalCapex },
    { name: 'Contingency', amount: a.contingencyCapex },
  ];

  const data = items.map((item) => ({
    name: item.name,
    value: parseFloat(((item.amount / total) * 100).toFixed(1)),
    amount: item.amount,
  }));

  return (
    <div>
      <ResponsiveContainer width="100%" height={180}>
        <PieChart>
          <Pie data={data} cx="50%" cy="50%" outerRadius={72} paddingAngle={2} dataKey="value">
            {data.map((_, idx) => (
              <Cell key={`capex-cell-${idx}`} fill={COLORS[idx % COLORS.length]} strokeWidth={0} />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend
            wrapperStyle={{ fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.06em' }}
            iconType="circle"
            iconSize={7}
            formatter={(v) => <span style={{ color: 'var(--text-secondary)' }}>{v}</span>}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}
